import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { useCritiqueCheckpointCompletion } from 'src/hooks/useCritiqueGroups';
import { CritiqueAvatar } from 'src/components/critiqueGroups/critiqueAvatar';
import { MemberName } from 'src/components/critiqueGroups/memberName';
import { userRefName } from 'src/utils/critiqueGroupUtil';

/**
 * Signature UX #3 — the "who's done" pulse (design D1 / H4).
 *
 * Deliberately calm: a ring, names, and a soft "not yet" — no red, no shaming,
 * no per-member timestamps. It answers "is the circle moving?", not "who is
 * slacking". `done` (not `met`) is what's shown, so a guiding checkpoint reads
 * as done for everyone, exactly as the server treats it.
 */

const Ring = styled.div<{ $pct: number; $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $pct }) =>
    `conic-gradient(#00b5ad ${$pct * 360}deg, #eceae2 ${$pct * 360}deg)`};
`;

const RingInner = styled.div<{ $size: number }>`
  width: ${({ $size }) => Math.round($size * 0.72)}px;
  height: ${({ $size }) => Math.round($size * 0.72)}px;
  border-radius: 50%;
  background: var(--br-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: ${({ $size }) => Math.round($size * 0.22)}px;
  color: #00817b;
`;

const SectionLabel = styled.div<{ $tone: 'done' | 'not' }>`
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 12px 0 4px;
  color: ${({ $tone }) => ($tone === 'done' ? '#2fa84f' : '#c88a1a')};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  /* Matches the equivalent roster row in hostOverview: a name plus a trailing
     control shouldn't be forced onto one line on a phone. */
  flex-wrap: wrap;
  & + & {
    border-top: 1px solid #f4efe4;
  }
`;

/** Compact ring for the overview card — fetches its own completion. */
export const CheckpointDonePulse: React.FC<{
  groupId: string;
  checkpointId: string;
  size?: number;
}> = ({ groupId, checkpointId, size = 40 }) => {
  const { data, isLoading } = useCritiqueCheckpointCompletion(groupId, checkpointId);
  const rows = data?.completion || [];
  const done = rows.filter(r => r.done).length;
  const total = rows.length;
  const pct = total > 0 ? done / total : 0;

  return (
    <Ring $pct={isLoading ? 0 : pct} $size={size} data-testid="checkpoint-done-pulse">
      <RingInner $size={size}>{isLoading ? '—' : `${done}/${total}`}</RingInner>
    </Ring>
  );
};

/**
 * The full per-checkpoint roster (design H4). Host/moderator only — the API
 * 403s anyone else, so only mount this for them.
 */
export const WhosDone: React.FC<{ groupId: string; checkpointId: string }> = ({
  groupId,
  checkpointId,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCritiqueCheckpointCompletion(
    groupId,
    checkpointId
  );

  if (isLoading) {
    return (
      <div style={{ fontSize: 12, color: '#a49f93', marginTop: 9 }}>
        <Icon name="spinner" loading />
        {t('CritiqueLoadingWhosDone', 'Checking who’s done…')}
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div style={{ fontSize: 12, color: '#a49f93', marginTop: 9 }}>
        {t('CritiqueWhosDoneUnavailable', 'Progress isn’t available right now.')}
      </div>
    );
  }

  const rows = data.completion || [];
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#a49f93', marginTop: 9 }}>
        {t('CritiqueNoMembersToTrack', 'No members yet — invite readers to get moving.')}
      </div>
    );
  }

  const done = rows.filter(r => r.done);
  const notYet = rows.filter(r => !r.done);

  return (
    <div data-testid="whos-done">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 11 }}>
        <Ring $pct={rows.length ? done.length / rows.length : 0} $size={40}>
          <RingInner $size={40}>
            {done.length}/{rows.length}
          </RingInner>
        </Ring>
        <div style={{ fontSize: 12, color: '#6b675e' }}>
          {done.length === rows.length
            ? t('CritiqueEveryoneDone', 'Everyone’s here 🎉')
            : t('CritiqueNOfMDone', '{{done}} of {{total}} done', {
                done: done.length,
                total: rows.length,
              })}
        </div>
      </div>

      {done.length > 0 && (
        <>
          <SectionLabel $tone="done">{t('CritiqueDone', 'Done')}</SectionLabel>
          {done.map(r => (
            <Row key={r.user._id}>
              <CritiqueAvatar id={r.user._id} name={r.user.displayName} />
              <MemberName>{userRefName(r.user, t)}</MemberName>
              <Icon name="check circle" style={{ color: '#2fa84f' }} />
            </Row>
          ))}
        </>
      )}

      {notYet.length > 0 && (
        <>
          <SectionLabel $tone="not">{t('CritiqueNotYet', 'Not yet')}</SectionLabel>
          {notYet.map(r => (
            <Row key={r.user._id}>
              <CritiqueAvatar id={r.user._id} name={r.user.displayName} muted />
              <MemberName style={{ color: '#8a857b' }}>
                {userRefName(r.user, t)}
              </MemberName>
              <Icon name="clock outline" style={{ color: '#c88a1a' }} />
            </Row>
          ))}
        </>
      )}
    </div>
  );
};

export default WhosDone;
